import React, { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { PAY_TABS, PairTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { useLayer } from '../components/layers'
import { usePickerDismiss } from '../components/pickerDismiss'
import { SkeletonRows } from '../components/pending'
import { AsAt } from '../components/AsAt'
import { useRefetch } from '../lib/connection'
import { useLive } from '../lib/live'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { can, hasPower, useAccess } from '../lib/access'
import { useHelpPref, usePreference } from '../lib/preferences'
import styles from './WagesPage.module.css'
import { ExportModal } from './wages/ExportModal'
import { PeriodPicker } from './wages/PeriodPicker'
import { datesLabel, readKept, useKeptDates } from '../lib/keptDates'
import { StillLooking } from '../components/StillLooking'
import { todayLocal } from './timesheets/time'
import { QueuePanel } from './wages/QueuePanel'
import { RecalcModal } from './wages/RecalcModal'
import { SettingsModal } from './wages/SettingsModal'
import { WageDrawer } from './wages/WageDrawer'
import { PeriodReviewBanner } from '../components/ExportReviewNotices'
import { PushAttempts } from './wages/PushAttempts'
import { HowItWorks } from './wages/HowItWorks'
import { SCOPE_HELP, SCOPE_LABEL, blockedLabel, clock, dmy, hrs, kg, money, plural, rangeLabel, stateOf } from './wages/format'
import type { Scope, WageGroup, WagesResponse } from './wages/types'

// Wages (CONTROL V4 §7j). Every hours and money figure is the API's canonical
// wage result (wage-calc.js); this screen filters, arranges and explains it.
// A blocked row has no figure at all: it shows why, and it is in no total.

// The Rows filter (V4 FILTER_SECTIONS), keyed to the API's `status` values.
// "Allocation undetermined" is this build's addition: blocked rows are real
// rows with no figure, so they need a way to be listed on their own.
const ROWS: [string, string][] = [
  ['', 'All rows'],
  ['topup', 'Top-up required'],
  ['overlap', 'Overlapping sessions'],
  ['norate', 'No piece rate'],
  ['blocked', 'Allocation undetermined'],
  ['in_progress', 'In progress'],
  ['stale', 'Recalculating'],
]

interface Col {
  key: string
  label: string
  w: number
  num?: boolean
  wrap?: boolean
}

// V4 wgColSet: the primary columns per scope, then the optional ones.
function colSet(scope: Scope, chosen: string[]) {
  const extra: (Col & { hide?: boolean })[] = [
    { key: 'contractor', label: 'Contractor', w: 150 },
    { key: 'workType', label: 'Work type', w: 112, hide: scope === 'combined' },
    { key: 'start', label: 'Start', w: 76, hide: scope !== 'daily' },
    { key: 'finish', label: 'Finish', w: 76, hide: scope !== 'daily' },
    { key: 'paid', label: 'Paid hours', w: 100, num: true },
    { key: 'breaks', label: 'Paid breaks', w: 108, num: true },
    { key: 'eff', label: 'Effective $/hr', w: 120, num: true, hide: scope === 'combined' },
    { key: 'minRate', label: 'Min rate', w: 96, num: true },
    { key: 'items', label: 'Items', w: 76, num: true },
    { key: 'kgph', label: 'kg / hour', w: 96, num: true },
    { key: 'sessions', label: 'Sessions', w: 90, num: true },
    { key: 'picking', label: 'Picking summary', w: 300, wrap: true },
    { key: 'product', label: 'Product', w: 170 },
  ]
  const shown = extra.filter((c) => !c.hide)
  const base: Col[] = [{ key: 'worker', label: 'Worker', w: 166 }]
  if (scope === 'daily') base.push({ key: 'date', label: 'Date', w: 104 })
  if (scope !== 'combined') base.push({ key: 'team', label: 'Team', w: 156 })
  base.push({ key: 'hours', label: 'Hours', w: 92, num: true }, { key: 'qty', label: 'Quantity', w: 104, num: true }, { key: 'earned', label: 'Earned', w: 106, num: true })
  if (scope === 'combined') base.push({ key: 'effc', label: 'Effective $/hr', w: 120, num: true })
  base.push({ key: 'minReq', label: 'Min required', w: 118, num: true }, { key: 'topUp', label: 'Top-up', w: 100, num: true }, { key: 'wages', label: 'Wages', w: 116, num: true })
  return { cols: base.concat(shown.filter((c) => chosen.includes(c.key))), extra: shown }
}

export const tableMinWidth = (cols: { w: number }[]) => cols.reduce((a, c) => a + c.w, 0) + 12 * cols.length + 32 + 96
const rem = (px: number) => `${px / 16}rem`

// The primary money run a blocked row replaces with its reason, and the
// optional columns that are also figures (shown as "—" on a blocked row).
const RUN = new Set(['hours', 'qty', 'earned', 'effc', 'minReq', 'topUp', 'wages'])
const FIGURE = new Set(['paid', 'breaks', 'eff', 'minRate', 'kgph'])

function cell(g: WageGroup, key: string): string {
  switch (key) {
    case 'worker':
      return g.worker_name
    case 'date':
      return dmy(g.work_date)
    case 'team':
      return g.team_name || '—'
    case 'hours':
      return hrs(g.hours)
    case 'qty':
      return kg(g.qty_kg)
    case 'earned':
      return money(g.earned)
    case 'eff':
    case 'effc':
      return money(g.effective_rate) + '/h'
    case 'minReq':
      return money(g.min_required)
    case 'topUp':
      return g.top_up ? money(g.top_up) : '—'
    case 'wages':
      return money(g.wages)
    case 'contractor':
      return g.contractor || '—'
    case 'workType':
      return g.work_types.join(', ') || '—'
    case 'start':
      return clock(g.start, g.work_date)
    case 'finish':
      return clock(g.finish, g.work_date)
    case 'paid':
      return hrs(g.paid_hours)
    case 'breaks':
      return g.breaks ? money(g.breaks) : '—'
    case 'minRate':
      return money(g.min_rate)
    case 'items':
      return g.items ? String(g.items) : '—'
    case 'kgph':
      return g.kg_per_hour ? g.kg_per_hour.toFixed(1) : '—'
    case 'sessions':
      return String(g.sessions)
    case 'picking':
      return g.picking_summary || '—'
    case 'product':
      return g.products.join(' + ') || '—'
  }
  return ''
}

// Column choices are the signed-in person's, kept on the server (h 3137-3141;
// preference `wages.columns`), so they follow them to another machine.
const NO_COLS: string[] = []

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

// The range picked here is kept for the rest of the browser tab today, per
// sign-in, so going to Timesheets and back lands on it again
// (lib/keptDates.ts). A new tab or a new day opens on the current pay period.
const PERIOD_KEY = 'wages.period'
type Range = { from: string; to: string }
const validRange = (r: any): r is Range => !!r && DAY_RE.test(r.from) && DAY_RE.test(r.to) && r.from <= r.to

type Overlay = null | 'cal' | 'filters' | 'cols' | 'cog' | 'queue' | 'recalc' | 'export'

export function WagesPage() {
  const { user } = useAuth()
  // D1.1/D1.6: Recalculate needs Wages edit, the cog's settings Wages add, and
  // Export payroll its own power (ADMIN, PAYROLL). The server refuses anyway.
  const access = useAccess()
  const mayRecalc = can(access, 'wages', 'edit')
  const mayConfigure = can(access, 'wages', 'add')
  const mayExport = hasPower(access, 'payroll_export')
  const uid = user?.uid
  const userRef = useRef(user)
  userRef.current = user
  const seq = useRef(0)

  // No range picked yet: the API opens on the tenant's current pay period
  // (h 3484-3485) and says which it chose, so the page never guesses it.
  // A link from an export notice names the period and the export (D6.5).
  const link = useMemo(() => new URLSearchParams(window.location.search), [])
  const linkFrom = link.get('from') || ''
  const linkTo = link.get('to') || ''
  const today = todayLocal()
  const [picked, setPicked] = useState<Range | null>(() => {
    if (validRange({ from: linkFrom, to: linkTo })) return { from: linkFrom, to: linkTo }
    const k = readKept(PERIOD_KEY, uid, today, validRange)
    return k ? { from: k.from, to: k.to } : null
  })
  const kept = useKeptDates(PERIOD_KEY, uid, today, picked)
  const [scopeSel, setScopeSel] = useState<Scope | null>(null)
  const [query, setQuery] = useState('')
  const [q, setQ] = useState('')
  const [teamId, setTeamId] = useState('')
  const [contractor, setContractor] = useState('')
  const [status, setStatus] = useState('')
  const [attn, setAttn] = useState(false)
  const [cols, setCols] = usePreference<string[]>(user, 'wages.columns', NO_COLS)
  const [help, setHelp] = useHelpPref(user, 'wages')

  const [data, setData] = useState<WagesResponse | null>(null)
  // The period the rows shown were loaded for ('current' until one is
  // picked). Until it is the period asked for, the table body is skeleton rows
  // (h 3994-4002); a filter change or a reload after a save keeps the rows up.
  const [shownKey, setShownKey] = useState<string | null>(null)
  const periodKey = picked ? `${picked.from}_${picked.to}` : 'current'
  const [asAt, setAsAt] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState('')
  const [overlay, setOverlay] = useState<Overlay>(link.get('export') && mayExport ? 'export' : null)
  const [openKey, setOpenKey] = useState<string | null>(null)
  // The pay-period button and its popover share this wrapper (h 440-447).
  const calWrap = usePickerDismiss(overlay === 'cal', () => setOverlay((o) => (o === 'cal' ? null : o)))
  // The Filters and Columns popovers are layers (h 425-458); the drawer and
  // the modals register their own, so one Escape closes the topmost.
  useLayer(overlay === 'filters', () => setOverlay(null))
  useLayer(overlay === 'cols', () => setOverlay(null))

  // The search box narrows server-side, a beat after typing stops.
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  // The query the rows on screen answer: a refetch of it that fails keeps them (and any modal over them) up and says so; a different
  // period or filter that fails shows none of the old rows.
  const dataKey = useRef<string | null>(null)
  const load = useCallback(async () => {
    const user = userRef.current
    if (!user) return
    const n = ++seq.current
    setError(null)
    const qs = new URLSearchParams(picked || {})
    if (scopeSel) qs.set('mode', scopeSel)
    if (status) qs.set('status', status)
    if (q) qs.set('q', q)
    if (teamId) qs.set('team_id', teamId)
    if (contractor) qs.set('contractor', contractor)
    if (attn) qs.set('attention', '1')
    try {
      const r = await apiJson<WagesResponse>(user, `/api/wages?${qs}`)
      if (n !== seq.current) return
      dataKey.current = qs.toString()
      setData(r)
      setAsAt(new Date())
    } catch (e: any) {
      if (n !== seq.current) return
      // The rows on screen were for the period or filters before this one; they must not stay under the new ones.
      if (dataKey.current !== qs.toString()) {
        dataKey.current = null
        setData(null)
      }
      setError(e?.message || 'Could not load wages')
    } finally {
      if (n === seq.current) setShownKey(picked ? `${picked.from}_${picked.to}` : 'current')
    }
  }, [uid, picked, scopeSel, status, q, teamId, contractor, attn])

  useEffect(() => {
    load()
  }, [load])
  // h 4167-4171: it fetches again on window focus and on reconnect. And the
  // queue chip, the banner and the row markers light up without the user
  // doing anything (h 4162-4166): a day in the period queued or rebuilt is
  // pushed, and the page fetches the basis as it now stands. It stays stamped
  // "as at": it is a period, not today.
  useRefetch(load)
  const period = data?.period
  useLive(user, ['wages'], period ? (d) => d >= period.from && d <= period.to : null, load)

  // A background task finishes later, perhaps after the period or filters
  // have moved: it reloads what is on screen then, not what was when it began.
  const loadRef = useRef(load)
  loadRef.current = load
  const refresh = useCallback(() => {
    loadRef.current()
  }, [])
  // A push (or one that ended unknown) has finished: the push record reloads too.
  const [pushTick, setPushTick] = useState(0)
  const onExported = useCallback(() => {
    refresh()
    setPushTick((n) => n + 1)
  }, [refresh])

  const scope: Scope = data?.scope || scopeSel || 'daily'
  const set = useMemo(() => colSet(scope, cols), [scope, cols])
  // Widths are the design's px at the default text size, drawn in rem so the
  // columns grow with larger text rather than wrapping it (h 1553-1555).
  const grid = set.cols.map((c) => rem(c.w)).join(' ') + ' minmax(6rem, 1fr)'
  // The row's own floor: the columns, a 12px gap between each, 16px padding
  // either side and the flexible last column. The page takes it as its width
  // floor (--wg-table-min) so the shell's content wrapper is the one
  // horizontal scroller (§7j.4b); the card itself never scrolls sideways.
  const minWidth = rem(tableMinWidth(set.cols))

  const groups = data?.groups || []
  const totals = data?.totals
  const queue = data?.queue || { queued: 0, failed: 0, items: [] }
  const open = groups.find((g) => g.key === openKey) || null
  const settings = data?.settings
  const shown = picked || data?.period || null
  const from = shown?.from || ''
  const to = shown?.to || ''

  const marked = useMemo(() => new Set([...groups.flatMap((g) => g.dates), ...(data?.live || []).map((l) => l.work_date)]), [groups, data])

  // Everyone with work in view: the recalculation picker's worker list.
  const workers = useMemo(() => {
    const m = new Map<string, string>()
    const add = (id: string, name: string) => id && !m.has(id) && m.set(id, name)
    groups.forEach((g) => add(g.human_id, g.worker_name))
    ;(data?.live || []).forEach((r) => add(r.human_id, r.worker_name))
    ;(data?.rate_issues || []).forEach((r) => add(r.human_id, r.worker_name))
    ;(data?.excluded_days || []).forEach((r) => add(r.human_id, r.worker_name))
    ;(data?.unattributed || []).forEach((r) => add(r.human_id, r.worker_name))
    ;(data?.not_calculated_days || []).forEach((r) => add(r.human_id, r.worker_name))
    queue.items.forEach((r) => add(r.human_id, r.worker_name))
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [groups, data, queue.items])

  const filterCount = [teamId, contractor, status].filter(Boolean).length
  const clearOn = !!(query || filterCount || attn)
  const clearAll = () => {
    setQuery('')
    setQ('')
    setTeamId('')
    setContractor('')
    setStatus('')
    setAttn(false)
    setOpenKey(null)
  }

  const done = (msg: string) => {
    setOverlay(null)
    setFlash(msg)
    load()
  }

  const toggleCol = (key: string) => {
    setCols(cols.includes(key) ? cols.filter((k) => k !== key) : cols.concat(key))
  }

  const liveDays = data?.live_days || []
  const days = Object.keys(data?.day_states || {}).length
  const setLine = settings
    ? (scope === 'daily' || settings.merged_assessment === 'per_day' ? 'Top-ups per day' : 'Top-ups over the period') +
      (settings.default_min_rate != null ? ' · minimum ' + money(settings.default_min_rate) + '/h' : ' · no default minimum rate')
    : ''
  const rowNoun = scope === 'daily' ? ['worker/day/team row', 'worker/day/team rows'] : scope === 'team' ? ['worker/team row', 'worker/team rows'] : ['worker row', 'worker rows']
  const blockedN = totals?.blocked || 0

  const loading = shownKey !== periodKey

  const headerActions = (
    <>
      {queue.items.length > 0 && (
        <button type="button" className={[styles.queueChip, queue.failed ? styles.queueChipBad : ''].join(' ')} title="Recalculation queue" onClick={() => setOverlay('queue')}>
          ↻ {queue.items.length} queued{queue.failed ? ` · ${queue.failed} failed` : ''}
        </button>
      )}
      {mayRecalc && (
        <button type="button" className={headerStyles.btn} onClick={() => setOverlay('recalc')}>
          ↻ Recalculate…
        </button>
      )}
      {mayExport && (
        <button type="button" className={`${headerStyles.btn} ${headerStyles.btnPrimary}`} onClick={() => setOverlay('export')}>
          Export payroll
        </button>
      )}
      <button
        type="button"
        className={headerStyles.btnHelp}
        aria-expanded={help}
        aria-controls="wages-how-it-works"
        onClick={() => {
          setHelp(!help)
        }}
      >
        {help ? 'Hide help' : 'How wages are worked out'}
      </button>
      {mayConfigure && (
        <button type="button" className={headerStyles.iconBtn} title="Wage settings: minimum rate, top-up scope and paid-break allocation" aria-label="Wage settings" onClick={() => setOverlay('cog')} disabled={!settings}>
          ⚙
        </button>
      )}
    </>
  )

  return (
    <div className={styles.page} data-testid="wages-page" style={{ '--wg-table-min': minWidth } as CSSProperties}>
      {picked && (
        <StillLooking
          at={kept.at}
          onDefaults={!!data?.current_period && picked.from === data.current_period.from && picked.to === data.current_period.to}
          label={datesLabel(picked.from, picked.to, today)}
          onContinue={kept.confirm}
          onDefault={() => setPicked(null)}
          defaultLabel="Show this pay period"
        />
      )}
      <PageHeader
        title="Wages"
        actions={headerActions}
        tabs={<PairTabs label="Pay" tabs={PAY_TABS} current="Wages" />}
      />

      <PageBody>
        {help && <HowItWorks onClose={() => setHelp(false)} />}

        {/* They change what the numbers mean, so they are read before the
            controls and the table (h 1985-1989). */}
        {shown && <PeriodReviewBanner from={shown.from} to={shown.to} />}
        <Notices data={data} onRecalc={() => setOverlay('recalc')} />
        <PushAttempts tick={pushTick} mayReconcile={mayExport} focusId={link.get('attempt')} onChanged={refresh} />

        <div className={styles.toolbar}>
        <div className={styles.popWrap} ref={calWrap}>
          <button type="button" className={styles.periodBtn} onClick={() => setOverlay(overlay === 'cal' ? null : 'cal')} aria-expanded={overlay === 'cal'}>
            <span>Pay period</span>
            <span className={styles.periodLabel}>{rangeLabel(from, to)}</span>
          </button>
          {overlay === 'cal' && shown && (
            <PeriodPicker
              from={from}
              to={to}
              marked={marked}
              today={data?.today}
              payPeriod={settings?.pay_period}
              onClose={() => setOverlay(null)}
              onApply={(f, t) => {
                setPicked({ from: f, to: t })
                setOpenKey(null)
                setOverlay(null)
              }}
            />
          )}
        </div>
        {liveDays.length > 0 && (
          <span
            className={styles.liveChip}
            data-testid="live-chip"
            title="This period still includes a live day: devices are reporting, so wages for it are provisional and will change as clock-offs arrive. The rest of the period is closed or locked and will not move."
          >
            <span className={styles.liveDot} />
            Live
            {days > 1 && <span className={styles.liveCount}>{liveDays.length} of {days} days</span>}
          </span>
        )}
        {!loading && <AsAt at={asAt} />}
        <div className={styles.toolbarEnd}>
          <span className={styles.setLine}>{setLine}</span>
        </div>
      </div>

      <div className={styles.toolbar}>
        <input className={styles.search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search workers…" aria-label="Search workers" />
        <div className={styles.popWrap}>
          <button type="button" className={[styles.filterBtn, overlay === 'filters' || filterCount ? styles.filterBtnOn : ''].join(' ')} onClick={() => setOverlay(overlay === 'filters' ? null : 'filters')} aria-expanded={overlay === 'filters'}>
            Filters
            {filterCount > 0 && <span className={styles.filterCount}>{filterCount}</span>}
          </button>
          {overlay === 'filters' && (
            <div className={styles.filterPop} role="dialog" aria-label="Filters">
              <FilterSection label="Team" value={teamId} options={[['', 'All teams'], ...(data?.teams || []).map((t) => [t.id, t.name] as [string, string])]} onPick={(v) => (setTeamId(v), setOpenKey(null))} />
              <FilterSection label="Contractor" value={contractor} options={[['', 'All contractors'], ...(data?.contractors || []).map((c) => [c, c] as [string, string])]} onPick={(v) => (setContractor(v), setOpenKey(null))} />
              <FilterSection label="Rows" value={status} options={ROWS} onPick={(v) => (setStatus(v), setOpenKey(null))} />
              <div className={styles.filterFoot}>
                <span>{filterCount === 0 ? 'Showing every row in the period' : plural(groups.length, 'row matches', 'rows match')}</span>
                {(filterCount > 0 || attn) && (
                  <button
                    type="button"
                    className={styles.popBtn}
                    onClick={() => {
                      // Releases the attention shortcut with the rest (h 1946-1947).
                      setTeamId('')
                      setContractor('')
                      setStatus('')
                      setAttn(false)
                      setOpenKey(null)
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Beside Filters, as on Timesheets (h 1942-1948). The count is taken
            before the shortcut applies, so it never reports its own result. */}
        {((data?.attn || 0) > 0 || attn) && (
          <button
            type="button"
            className={[styles.attnChip, attn ? styles.attnOn : ''].join(' ')}
            aria-pressed={attn}
            title="Rows whose figure is not final: overlapping sessions, unpriced picking, or a queued recalculation"
            onClick={() => {
              setAttn(!attn)
              setOpenKey(null)
            }}
          >
            {data?.attn || 0} need attention{attn ? ' ×' : ''}
          </button>
        )}
        <div className={styles.popWrap}>
          <button type="button" className={styles.filterBtn} onClick={() => setOverlay(overlay === 'cols' ? null : 'cols')} aria-expanded={overlay === 'cols'}>
            {cols.length ? `Columns · ${cols.length} added` : 'Columns'}
          </button>
          {overlay === 'cols' && (
            <div className={styles.colsPop} role="dialog" aria-label="Detail columns">
              <div className={styles.popLabel}>Detail columns</div>
              {set.extra.map((c) => {
                const on = cols.includes(c.key)
                return (
                  <button key={c.key} type="button" role="checkbox" aria-checked={on} className={[styles.colOpt, on ? styles.colOptOn : ''].join(' ')} onClick={() => toggleCol(c.key)}>
                    <span className={[styles.colBox, on ? styles.colBoxOn : ''].join(' ')}>{on ? '✓' : ''}</span>
                    {c.label}
                  </button>
                )
              })}
              <button
                type="button"
                className={`${styles.popBtn} ${styles.colsReset}`}
                onClick={() => setCols([])}
              >
                Reset to primary columns
              </button>
            </div>
          )}
        </div>
        {clearOn && (
          <button type="button" className={styles.linkBtn} onClick={clearAll}>
            Clear filters
          </button>
        )}
        <div className={styles.toolbarEnd}>
          <span className={styles.summary}>{plural(groups.length, rowNoun[0], rowNoun[1])}</span>
          {totals && (
            <span className={styles.muted} data-testid="totals-line">
              {hrs(totals.hours)} · {money(totals.earned)} earned · {money(totals.top_up)} top-up · {money(totals.wages)} payable
            </span>
          )}
        </div>
      </div>

      {flash && (
        <div className={styles.flash} role="status">
          {flash}
          <button type="button" className={styles.flashX} aria-label="Dismiss" onClick={() => setFlash('')}>
            ×
          </button>
        </div>
      )}
      {error && (
        <div className={styles.errorBar} role="alert">
          <span>{data ? `Wages could not be refreshed: ${error}. What is shown may be out of date.` : error}</span>
          <button type="button" className={styles.flashX} onClick={() => load()}>
            Retry
          </button>
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div className={styles.tabTrack} role="tablist" aria-label="Scope">
            {(['daily', 'team', 'combined'] as Scope[]).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={scope === s}
                className={[styles.tab, scope === s ? styles.active : ''].join(' ')}
                onClick={() => {
                  setScopeSel(s)
                  setOpenKey(null)
                }}
              >
                {SCOPE_LABEL[s]}
              </button>
            ))}
          </div>
          <span className={styles.scopeHelp}>{SCOPE_HELP[scope]}</span>
        </div>
        <div className={styles.table} role="table" aria-label="Wages">
          <div className={styles.headRow} role="row" style={{ gridTemplateColumns: grid, minWidth }}>
            {set.cols.map((c) => (
              <span key={c.key} role="columnheader" className={c.num ? styles.num : ''}>
                {c.label}
              </span>
            ))}
            <span role="columnheader" />
          </div>
          {loading ? (
            <SkeletonRows rowClass={styles.row} rowStyle={{ gridTemplateColumns: grid, minWidth }} cols={set.cols.length + 1} label="Loading wages" />
          ) : groups.length === 0 ? (
            <div className={styles.empty}>{error && !data ? 'Wages could not be loaded, so no rows are shown.' : status === 'in_progress' ? 'Open sessions are listed above. They are operational, not final wages.' : 'No wage basis rows match. Widen the period or clear the filters.'}</div>
          ) : (
            groups.map((g) => (
              <Row key={g.key} g={g} cols={set.cols} grid={grid} minWidth={minWidth} open={openKey === g.key} onOpen={() => setOpenKey(g.key)} />
            ))
          )}
          {totals && (
            <div className={styles.footRow} role="row" style={{ gridTemplateColumns: grid, minWidth }}>
              {set.cols.map((c, i) => (
                <span key={c.key} className={c.num ? styles.num : ''}>
                  {i === 0
                    ? `Total · ${plural(totals.groups, 'row', 'rows')}`
                    : c.key === 'hours'
                      ? hrs(totals.hours)
                      : c.key === 'paid'
                        ? hrs(totals.paid_hours)
                        : c.key === 'qty'
                          ? kg(totals.qty_kg)
                          : c.key === 'earned'
                            ? money(totals.earned)
                            : c.key === 'minReq'
                              ? money(totals.min_required)
                              : c.key === 'topUp'
                                ? money(totals.top_up)
                                : c.key === 'wages'
                                  ? money(totals.wages)
                                  : ''}
                </span>
              ))}
              <span className={styles.footNote}>{blockedN ? `${blockedN} blocked, not in totals` : ''}</span>
            </div>
          )}
        </div>
      </div>
      </PageBody>

      {open && (
        <WageDrawer
          user={user}
          group={open}
          scope={scope}
          from={from}
          to={to}
          queue={queue.items}
          onClose={() => setOpenKey(null)}
          onQueued={(m) => {
            setFlash(m)
            load()
          }}
        />
      )}
      {overlay === 'cog' && settings && (
        <SettingsModal
          user={user}
          settings={settings}
          scope={scope}
          today={data?.today}
          onClose={() => setOverlay(null)}
          onSaved={(msg, periodChanged) => {
            // A new pay period moves the page onto it, whatever range was picked.
            if (!periodChanged || !picked) return done(msg)
            setOverlay(null)
            setFlash(msg)
            setPicked(null)
          }}
        />
      )}
      {overlay === 'queue' && <QueuePanel user={user} queue={queue} onClose={() => setOverlay(null)} onQueueMore={() => setOverlay('recalc')} onDone={done} />}
      {/* Both run as background tasks: the finish toast is the message, so
          finishing only reloads whatever the page is showing by then. */}
      {overlay === 'recalc' && shown && <RecalcModal user={user} from={from} to={to} workers={workers} onClose={() => setOverlay(null)} onFinished={refresh} />}
      {overlay === 'export' && shown && (
        <ExportModal user={user} from={from} to={to} scope={scope} payMode={settings?.pay_mode ?? null} onClose={() => setOverlay(null)} onRecalculate={() => setOverlay('recalc')} onExported={onExported} />
      )}
    </div>
  )
}

function FilterSection({ label, value, options, onPick }: { label: string; value: string; options: [string, string][]; onPick: (v: string) => void }) {
  return (
    <div className={styles.filterSection}>
      <span className={styles.popLabel}>{label}</span>
      <div className={styles.fchips}>
        {options.map(([v, text]) => (
          <button key={v || 'all'} type="button" aria-pressed={value === v} className={[styles.fchip, value === v ? styles.fchipOn : ''].join(' ')} onClick={() => onPick(v)}>
            {text}
          </button>
        ))}
      </div>
    </div>
  )
}

function Row({ g, cols, grid, minWidth, open, onOpen }: { g: WageGroup; cols: Col[]; grid: string; minWidth: string; open: boolean; onOpen: () => void }) {
  const st = stateOf(g)
  const tone = open ? styles.rowOpen : g.blocked ? styles.rowBlocked : g.overlap ? styles.rowOverlap : g.no_rate ? styles.rowNoRate : ''
  const cells: React.ReactNode[] = []
  for (let i = 0; i < cols.length; i++) {
    const c = cols[i]
    if (g.blocked && RUN.has(c.key)) {
      // One cell spanning the whole primary figure run: the reason, not a $0.
      let span = 1
      while (i + span < cols.length && RUN.has(cols[i + span].key)) span++
      cells.push(
        <span key={c.key} role="cell" className={styles.blockedCell} style={{ gridColumn: `span ${span}` }} title={st.title}>
          {blockedLabel(g)}
        </span>
      )
      i += span - 1
      continue
    }
    const text = g.blocked && FIGURE.has(c.key) ? '—' : cell(g, c.key)
    const cls = [c.num ? styles.num : '', c.wrap ? styles.wrap : styles.clip, c.key === 'worker' || c.key === 'wages' ? styles.strong : '', c.key === 'topUp' && g.top_up ? styles.topUp : ''].join(' ')
    // QC has marked picks whose deduction waits for the day close: the
    // quantity and pay here will drop by it, so they are not yet to be paid.
    const held = c.key === 'qty' && g.qc_held ? g.qc_held : null
    cells.push(
      <span key={c.key} role="cell" className={cls} title={held ? `${text}, before ${kg(held.kg)} of QC deductions held until the day closes (${plural(held.picks, 'pick', 'picks')})` : c.wrap ? undefined : text}>
        {text}
        {held && (
          <span className={styles.qcHeld} data-testid="qc-held">
            −{kg(held.kg)} QC held
          </span>
        )}
      </span>
    )
  }
  return (
    <div
      role="row"
      tabIndex={0}
      className={[styles.row, tone].join(' ')}
      style={{ gridTemplateColumns: grid, minWidth }}
      data-state={g.state}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen())}
    >
      {cells}
      <span role="cell" className={styles.stateCell} style={{ color: st.tone }} title={st.title}>
        {st.text}
      </span>
    </div>
  )
}

// What affects the money and is not a row: work still in progress, picking
// with nothing to price it, production with no session to credit, and shifts
// the wage basis has not reached yet.
function Notices({ data, onRecalc }: { data: WagesResponse | null; onRecalc: () => void }) {
  if (!data) return null
  const { live, rate_issues: rate, unattributed, not_calculated: notCalc, not_calculated_days: notCalcDays = [], excluded_days: excluded, qc_held: held = [] } = data
  return (
    <>
      {held.length > 0 && (
        <div className={styles.warnBar} data-testid="qc-held-banner">
          <strong>
            QC deductions not yet taken off: {plural(held.reduce((t, h) => t + h.picks, 0), 'pick', 'picks')}, {kg(held.reduce((t, h) => t + h.kg, 0))}
          </strong>
          <span> Their day is still live, so QC’s deductions are held until it closes and these rows still pay the full weight. Close the day before paying them.</span>
          <div className={styles.noticeList}>
            {held.slice(0, 6).map((h) => (
              <span key={h.human_id + h.work_date + (h.team_id || '')}>
                {h.worker_name} · {dmy(h.work_date)}: {plural(h.picks, 'pick', 'picks')}, −{kg(h.kg)}
              </span>
            ))}
            {held.length > 6 && <span>and {held.length - 6} more</span>}
          </div>
        </div>
      )}
      {rate.length > 0 && (
        <div className={styles.warnBar} data-testid="rate-banner">
          <strong>{plural(rate.length, 'picking day', 'picking days')} with no piece rate</strong>
          <span>
            {' '}
            Paid at the hourly or minimum rate because no priced picking job covered them. If this subscription does not pay harvest hourly, that is underpayment.
          </span>
          <div className={styles.noticeList}>
            {rate.slice(0, 6).map((r) => (
              <span key={r.human_id + r.work_date + r.team_name}>
                {r.worker_name} · {dmy(r.work_date)} · {r.team_name}: {r.message}
              </span>
            ))}
            {rate.length > 6 && <span>and {rate.length - 6} more</span>}
          </div>
          <Link className={styles.linkBtn} to="/picking-jobs">
            Open Picking Jobs
          </Link>
        </div>
      )}
      {live.length > 0 && (
        <div className={styles.liveStrip} data-testid="live-strip">
          <strong>{plural(live.length, 'session', 'sessions')} still in progress</strong>
          <span> Not clocked off, so no final wage exists for them. They are left out of the table, the totals and any export. The hours and $/h are operational only: time since clock-on, breaks not taken out.</span>
          <div className={styles.noticeList}>
            {live.map((r) => {
              const h = r.elapsed_minutes != null ? r.elapsed_minutes / 60 : null
              return (
                <span key={r.human_id + r.work_date + (r.start_at || '')} data-testid="live-row">
                  {r.worker_name} · {r.team_name} · since {clock(r.start_at, r.work_date)}
                  {h != null ? ` · ${hrs(h)} so far` : ''} · {kg(r.qty_kg)}
                  {r.piece_earnings ? ` · ${money(r.piece_earnings)} piece so far` : ''}
                  {r.piece_earnings && h && h >= 0.25 ? ` · ≈ ${money(r.piece_earnings / h)}/h` : ''}
                </span>
              )
            })}
          </div>
        </div>
      )}
      {unattributed.length > 0 && (
        <div className={styles.notice}>
          <strong>{plural(unattributed.length, 'worker/day has', 'worker/days have')} picking with no session to credit it to.</strong> That production is not in any wage until a session covers it in Timesheets:{' '}
          {unattributed
            .slice(0, 6)
            .map((u) => `${u.worker_name} ${dmy(u.work_date)} (${plural(u.cycles, 'item', 'items')})`)
            .join(', ')}
          {unattributed.length > 6 ? ` and ${unattributed.length - 6} more` : ''}.
        </div>
      )}
      {notCalc > 0 && (
        <div className={styles.notice}>
          <strong>{plural(notCalc, 'worker/day in this period has', 'worker/days in this period have')} a shift but no wage basis yet</strong> and {notCalc === 1 ? 'is' : 'are'} not shown
          {notCalcDays.length ? ': ' + notCalcDays.slice(0, 6).map((d) => `${d.worker_name} ${dmy(d.work_date)}`).join(', ') + (notCalcDays.length > 6 ? ` and ${notCalcDays.length - 6} more` : '') : ''}.{' '}
          <button type="button" className={styles.linkBtn} onClick={onRecalc}>
            Queue a recalculation
          </button>
        </div>
      )}
      {excluded.length > 0 && <div className={styles.mutedBar}>{plural(excluded.length, 'worker/day is', 'worker/days are')} excluded from wages in Timesheets and not shown.</div>}
    </>
  )
}
